import { RADField } from '@symfa-inc/providence-verizon-types';
import { browseReducer } from '@core/utils/methods/browse-sorter-reducer';
import { transformFields } from '@core/utils/methods/transform-fields';
import { createReducer, on } from '@core/utils/store';
import {
  REGULATORY_COMPLIANCE_INFO_TRANSFORM_FIELDS,
  RFDS_INFO_TRANSFORM_FIELDS,
  SCOPING_SECTORS,
} from '@models/constants';
import {
  EngineeringBrowse,
  EngineeringLeasingInformation,
  EngineeringRegulatoryCompliance,
  EngineeringRFDSPhase1,
  EngineeringSOFTabData,
  EngineeringSummaryTabData,
  EngineeringWorkflowTabData,
  EngineeringWorkflowUpdateData,
  IdAndValue,
  OtherEquipmentData,
  PaginatedResponse,
  PayloadAndState,
  RadData,
  ScopingInfo,
  SiteBrowse,
  StructuralInformation,
  UpdateEngineeringSOFData,
} from '@models/interfaces';
import { CuttedProjectDetails, SectorDataState } from '@models/types';
import { EngineeringActions } from '@store/actions';
import {
  getOtherEquipmentInfo,
  getScopingSectorsFromUpdateData,
  getScopingSectorsInfo,
} from '../helpers';

type RADType = { [key in RADField]?: number };

type EngineeringProjectData = Omit<CuttedProjectDetails, 'civilVendor'> & {
  civilVendor?: IdAndValue | string;
};

export interface EngineeringBrowseState {
  engineeringBrowseData: PaginatedResponse<EngineeringBrowse>;
  engineeringSiteData: SiteBrowse;
  engineeringProjectData: EngineeringProjectData;
  engineeringLeasingData: EngineeringLeasingInformation;
  engineeringWorkflowData: EngineeringWorkflowUpdateData;
  otherEquipments: OtherEquipmentData[];
  equipmentTypeNames: string[];
  scopingSectors: SectorDataState;
  scopingInfo: Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>;
  isFetching: boolean;
  errors: boolean;
  RAD: RADType;
  loadingTabs: Record<string, boolean>;
  loadedTabs: Record<string, boolean>;
  tabErrors: Record<string, boolean>;
}

const initialState: EngineeringBrowseState = {
  engineeringSiteData: {} as SiteBrowse,
  engineeringProjectData: {} as EngineeringProjectData,
  engineeringLeasingData: {} as EngineeringLeasingInformation,
  engineeringWorkflowData: {} as EngineeringWorkflowUpdateData,
  engineeringBrowseData: { items: [], total: 0 },
  isFetching: false,
  errors: false,
  scopingInfo: {} as ScopingInfo,
  scopingSectors: SCOPING_SECTORS,
  otherEquipments: [],
  equipmentTypeNames: [],
  RAD: {
    [RADField.A]: undefined,
    [RADField.B]: undefined,
    [RADField.D]: undefined,
    [RADField.G]: undefined,
  },
  loadingTabs: {},
  loadedTabs: {},
  tabErrors: {},
};

export const reducer = createReducer(
  initialState,
  // GET ENGINEERING BROWSE DATA
  on(
    EngineeringActions.getEngineeringBrowseDataAction,
    browseReducer('engineeringBrowseData'),
  ),
  // SET TAB LOADING STATE
  on(
    EngineeringActions.setTabLoading,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoading: boolean },
      EngineeringBrowseState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: payload.isLoading,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  // SET TAB LOADED STATE
  on(
    EngineeringActions.setTabLoaded,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoaded: boolean },
      EngineeringBrowseState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      loadedTabs: {
        ...state.loadedTabs,
        [payload.tabName]: payload.isLoaded,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: false,
      },
    }),
  ),
  // SET TAB ERROR STATE
  on(
    EngineeringActions.setTabError,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; hasError: boolean },
      EngineeringBrowseState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: false,
      },
      tabErrors: {
        ...state.tabErrors,
        [payload.tabName]: payload.hasError,
      },
    }),
  ),
  // RESET TABS DATA
  on(
    EngineeringActions.resetTabsData,
    ({ state }: PayloadAndState<unknown, EngineeringBrowseState>) => ({
      ...state,
      loadingTabs: {},
      loadedTabs: {},
      tabErrors: {},
    }),
  ),
  // GET ENGINEERING SUMMARY TAB
  on(
    EngineeringActions.getEngineeringSummaryAction,
    ({
      payload: {
        site,
        leasing: leasingPayload,
        scoping: scopingPayload,
        ...engineeringProjectData
      },
      state,
    }: PayloadAndState<EngineeringSummaryTabData, EngineeringBrowseState>) => {
      const leasing = leasingPayload ?? {};
      const scopingData = scopingPayload ?? {};
      const { structural, ...restLeasingData } = leasing;

      return {
        ...state,
        engineeringProjectData: {
          ...state.engineeringProjectData,
          ...engineeringProjectData,
        } as EngineeringProjectData,
        engineeringSiteData: transformFields<SiteBrowse>(site, [
          'towerOwner',
          'riskCategory',
        ]),
        engineeringLeasingData: {
          ...state.engineeringLeasingData,
          ...restLeasingData,
          structural: structural
            ? transformFields<StructuralInformation>(structural, [
                'structuralResult',
              ])
            : state.engineeringLeasingData.structural,
        } as EngineeringLeasingInformation,
        scopingInfo: {
          ...state.scopingInfo,
          ...scopingData,
        } as Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>,
        loadingTabs: { ...state.loadingTabs, summary: false },
        loadedTabs: { ...state.loadedTabs, summary: true },
        tabErrors: { ...state.tabErrors, summary: false },
      };
    },
  ),
  // GET ENGINEERING WORKFLOW TAB
  on(
    EngineeringActions.getEngineeringWorkflowAction,
    ({
      payload: {
        site,
        leasing: leasingPayload,
        engineering: engineeringPayload,
        scoping: scopingPayload,
        ...engineeringProjectData
      },
      state,
    }: PayloadAndState<EngineeringWorkflowTabData, EngineeringBrowseState>) => {
      const leasing = leasingPayload ?? {};
      const engineering =
        engineeringPayload ?? ({} as EngineeringWorkflowUpdateData);
      const { RFDSPhase1, regulatoryCompliance } = engineering;
      const { PALOrNPARequired, ...restLeasingData } = leasing;

      return {
        ...state,
        engineeringProjectData: {
          ...state.engineeringProjectData,
          ...engineeringProjectData,
        } as EngineeringProjectData,
        engineeringSiteData: {
          ...state.engineeringSiteData,
          ...(transformFields<SiteBrowse>(site, [
            'riskCategory',
          ]) as SiteBrowse),
        },
        engineeringLeasingData: {
          ...state.engineeringLeasingData,
          ...restLeasingData,
          PALOrNPARequired,
        } as EngineeringLeasingInformation,
        scopingInfo: {
          ...state.scopingInfo,
          ...(scopingPayload ?? {}),
        } as Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>,
        engineeringWorkflowData: {
          RFDSPhase1: transformFields<EngineeringRFDSPhase1>(
            RFDSPhase1,
            RFDS_INFO_TRANSFORM_FIELDS,
          ) as EngineeringRFDSPhase1,
          regulatoryCompliance:
            transformFields<EngineeringRegulatoryCompliance>(
              {
                ...regulatoryCompliance,
                PALOrNPARequired: PALOrNPARequired ?? null,
              },
              REGULATORY_COMPLIANCE_INFO_TRANSFORM_FIELDS,
            ) as EngineeringRegulatoryCompliance,
        },
        loadingTabs: { ...state.loadingTabs, workflow: false },
        loadedTabs: { ...state.loadedTabs, workflow: true },
        tabErrors: { ...state.tabErrors, workflow: false },
      };
    },
  ),
  // GET ENGINEERING SOF TAB
  on(
    EngineeringActions.getEngineeringSOFAction,
    ({
      payload: {
        RAD = [],
        site,
        leasing: leasingPayload,
        engineering: engineeringPayload,
        scoping: scopingPayload,
        ...engineeringProjectData
      },
      state,
    }: PayloadAndState<EngineeringSOFTabData, EngineeringBrowseState>) => {
      const leasing = leasingPayload ?? {};
      const engineering =
        engineeringPayload ?? ({} as EngineeringWorkflowUpdateData);
      const scopingData = scopingPayload ?? ({} as ScopingInfo);
      const { leaseAudit, ...restLeasingData } = leasing;
      const { RFDSPhase1, regulatoryCompliance } = engineering;
      const { otherEquipments, scopingSectors, ...restScopingData } =
        scopingData;

      const otherEquipmentInfo = getOtherEquipmentInfo(otherEquipments ?? []);

      const scopingSectorsInfo = getScopingSectorsInfo(scopingSectors ?? []);

      return {
        ...state,
        scopingInfo: {
          ...state.scopingInfo,
          ...restScopingData,
        } as Omit<ScopingInfo, 'otherEquipments' | 'scopingSectors'>,
        scopingSectors:
          scopingSectorsInfo?.scopingSectors || initialState.scopingSectors,
        otherEquipments: otherEquipmentInfo?.otherEquipments || [],
        equipmentTypeNames: [
          ...new Set([
            ...(otherEquipmentInfo?.equipmentTypeNames || []),
            ...(scopingSectorsInfo?.equipmentTypeNames || []),
          ]),
        ],
        RAD: (Object.keys(state.RAD) as RADField[]).reduce(
          (acc: RADType, key: RADField) => ({
            ...acc,
            [key]: RAD?.find((RADData: RadData) => RADData.RADField === key)
              ?.scoping,
          }),
          {} as RADType,
        ),
        engineeringProjectData: {
          ...state.engineeringProjectData,
          ...engineeringProjectData,
        } as EngineeringProjectData,
        engineeringSiteData: {
          ...state.engineeringSiteData,
          ...(transformFields<SiteBrowse>(site, [
            'towerOwner',
            'riskCategory',
          ]) as SiteBrowse),
        },
        engineeringLeasingData: {
          ...state.engineeringLeasingData,
          ...restLeasingData,
          leaseAudit,
        } as EngineeringLeasingInformation,
        engineeringWorkflowData: {
          ...state.engineeringWorkflowData,
          RFDSPhase1: {
            ...state.engineeringWorkflowData?.RFDSPhase1,
            ...transformFields<EngineeringRFDSPhase1>(
              RFDSPhase1,
              RFDS_INFO_TRANSFORM_FIELDS,
            ),
          },
          regulatoryCompliance: {
            ...state.engineeringWorkflowData?.regulatoryCompliance,
            ...regulatoryCompliance,
          },
        },
        loadingTabs: { ...state.loadingTabs, sof: false },
        loadedTabs: { ...state.loadedTabs, sof: true },
        tabErrors: { ...state.tabErrors, sof: false },
      };
    },
  ),
  // UPDATE ENGINEERING DETAILS WORKFLOW TAB
  on(
    EngineeringActions.updateEngineeringWorkflowAction,
    ({
      payload: { RFDSPhase1, regulatoryCompliance },
    }: PayloadAndState<
      EngineeringWorkflowUpdateData,
      EngineeringBrowseState
    >) => ({
      engineeringWorkflowData: {
        RFDSPhase1,
        regulatoryCompliance,
      },
    }),
  ),
  // UPDATE ENGINEERING DETAILS SOF TAB
  on(
    EngineeringActions.updateEngineeringSOFAction,
    ({
      payload: { scopingNotes, projectInfo, otherEquipments, sectors },
      state,
    }: PayloadAndState<UpdateEngineeringSOFData, EngineeringBrowseState>) => {
      const projectInfoData = projectInfo ?? {};
      const scopingNotesInfo = scopingNotes ?? {};

      const {
        isCraneRequired,
        craneHeight,
        RADA,
        RADB,
        RADD,
        RADG,
        azimuthA,
        azimuthB,
        azimuthD,
        azimuthG,
        mountTypes,
        civilVendor,
      } = projectInfoData;
      const { RFDSStatus, ...notes } = scopingNotesInfo;

      return {
        RAD: {
          ...state.RAD,
          ...(RADA ? { A: RADA } : {}),
          ...(RADB ? { B: RADB } : {}),
          ...(RADG ? { G: RADG } : {}),
          ...(RADD ? { D: RADD } : {}),
        },
        scopingInfo: {
          ...state.scopingInfo,
          ...notes,
          ...(mountTypes?.length ? { previousMountModels: mountTypes } : {}),
        },
        engineeringProjectData: {
          ...state.engineeringProjectData,
          ...(civilVendor ? { civilVendor } : {}),
        },
        engineeringWorkflowData: {
          ...state.engineeringWorkflowData,
          RFDSPhase1: {
            ...state.engineeringWorkflowData?.RFDSPhase1,
            ...(RFDSStatus !== undefined && { RFDSStatus }),
          },
          regulatoryCompliance: {
            ...state.engineeringWorkflowData?.regulatoryCompliance,
            ...(isCraneRequired ? { isCraneRequired } : {}),
            ...(craneHeight ? { craneHeight } : {}),
          },
        },
        engineeringLeasingData: {
          ...state.engineeringLeasingData,
          leaseAudit: {
            ...state.engineeringLeasingData?.leaseAudit,
            ...(azimuthA ? { azimuthA } : {}),
            ...(azimuthB ? { azimuthB } : {}),
            ...(azimuthD ? { azimuthD } : {}),
            ...(azimuthG ? { azimuthG } : {}),
          },
        },
        otherEquipments,
        scopingSectors: getScopingSectorsFromUpdateData(sectors),
      };
    },
  ),
);
